import { hasWindow } from "@omni/utils/dom.utils";
import { cn } from "@omni/utils/styles";
import {
    MouseEvent,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useState
} from "react";

import { HeaderButtonContextProvider } from "@/components/Navigation/Buttons/HeaderButton/context/headerButton.context";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

export type HeaderButtonBaseProps = {
    icon: string;
    text?: string;
    alerts?: string;
    iconPrefix?: string;
    hover?: boolean;
    invert?: boolean;
    invertMobile?: boolean;
    hideChevron?: boolean;
    withOverlay?: boolean;
    link?: string;
    textHidden?: boolean;
    ariaLabel?: string;
    className?: string;
    menuElement?: ReactElement<{ closeCallback?: () => void }> | false | null;
};

type Props = HeaderButtonBaseProps & {
    children: ReactNode;
};

export const Base = ({
    icon,
    alerts,
    text,
    hover,
    invert,
    invertMobile,
    iconPrefix = "las",
    link,
    hideChevron = false,
    textHidden = false,
    withOverlay = false,
    ariaLabel,
    className,
    menuElement,
    children
}: Props) => {
    const [isActive, setActiveState] = useState<boolean>(false);
    const [preventBlur, setPreventBlur] = useState<boolean>(false);

    const setActive = useCallback((active: boolean = true) => {
        if (active && hasWindow()) {
            window?.dispatchEvent(new Event("header-button-activated"));
        }

        setActiveState(active);
    }, []);

    const handleOnActivated = useCallback(() => {
        setActive(false);
    }, [setActive]);

    const handleOnKeyDown = createKeyHandler([
        {
            keys: [KeyNames.ESCAPE],
            handler: () => setActive(false)
        }
    ]);

    const handleOnCloseMenu = useCallback(() => {
        setActive(false);
        setPreventBlur(false);
    }, [setActive]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener("header-button-activated", handleOnActivated, {
            signal: abortController.signal
        });
        window.addEventListener("keydown", handleOnKeyDown, {
            signal: abortController.signal
        });

        return () => {
            abortController.abort();
        };
    }, [handleOnKeyDown, handleOnActivated]);

    const onMouseEnter = () => {
        if (hover && menuElement) {
            setActive();
        }
        setPreventBlur(true);
    };

    const onMouseLeave = () => {
        if (hover && menuElement) {
            setActive(false);
        }
        setPreventBlur(false);
    };

    const handleOnClick = (event: MouseEvent) => {
        if (!hover && menuElement) {
            event.preventDefault();
            setActive(!isActive);
            setPreventBlur(true);
        }
    };

    const onBlur = () => {
        if (!preventBlur) {
            setActive(false);
        }
    };

    const overlayOpen = !!menuElement && isActive;

    return (
        <HeaderButtonContextProvider
            isActive={isActive}
            isOverlayOpen={overlayOpen}
            invert={!!invert}
            invertMobile={!!invertMobile}
            icon={icon}
            iconPrefix={iconPrefix}
            alerts={alerts}
            link={link}
            text={text}
            hover={hover}
            textHidden={textHidden}
            ariaLabel={ariaLabel}
            hideChevron={hideChevron}
            menuElement={menuElement}
            withOverlay={withOverlay}
            onClick={handleOnClick}
            onCloseMenu={handleOnCloseMenu}
        >
            <div
                className={cn(
                    "tw:relative",
                    {
                        "tw:layer-modal/[1]": isActive
                    },
                    className
                )}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onBlur={onBlur}
            >
                {children}
            </div>
        </HeaderButtonContextProvider>
    );
};
